const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const authService = {
  /**
   * Login user - tokens now stored in HTTP-only cookies
   * Returns { user, twoFactorRequired, twoFactorToken } if 2FA is needed
   */
  async login(email, password, twoFactorCode = null, twoFactorToken = null) {
    try {
      const body = { email, password };

      // Add 2FA fields if provided
      if (twoFactorCode && twoFactorToken) {
        body.twoFactorCode = twoFactorCode;
        body.twoFactorToken = twoFactorToken;
      }

      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // IMPORTANT: Send/receive cookies
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Login failed');
      }

      const data = await response.json();

      console.log('Login API Response:', data); // DEBUG

      // Check if 2FA is required
      if (data.twoFactorRequired) {
        console.log('2FA required for user');
        return {
          twoFactorRequired: true,
          twoFactorToken: data.twoFactorToken,
          email: data.email,
        };
      }

      // API returns user data directly (not nested under 'user' property)
      const user = data;

      console.log('Platform Role:', user.platformRole); // DEBUG

      // Tokens are now in HTTP-only cookies - don't store in localStorage!
      // Only store user data and roles
      localStorage.setItem('user', JSON.stringify(user));

      // Store company ID if present
      if (user.companyId) {
        localStorage.setItem('companyId', user.companyId);
      }

      // Store roles for portal redirection
      if (user.platformRole) {
        localStorage.setItem('platformRole', user.platformRole);
        console.log('Stored platformRole:', user.platformRole); // DEBUG
      }
      if (user.companyRole) {
        localStorage.setItem('companyRole', user.companyRole);
        console.log('Stored companyRole:', user.companyRole); // DEBUG
      }

      // Store subscription flags
      if (user.hasCmmsSubscription !== undefined) {
        localStorage.setItem('hasCmmsSubscription', user.hasCmmsSubscription.toString());
        console.log('Stored hasCmmsSubscription:', user.hasCmmsSubscription); // DEBUG
      }
      if (user.hasChatbotSubscription !== undefined) {
        localStorage.setItem('hasChatbotSubscription', user.hasChatbotSubscription.toString());
        console.log('Stored hasChatbotSubscription:', user.hasChatbotSubscription); // DEBUG
      }

      console.log('Is Platform Staff?', this.isPlatformStaff()); // DEBUG
      console.log('Portal URL:', this.getPortalUrl()); // DEBUG

      // Return the user data wrapped in the expected structure for LoginPage
      return { user: data, twoFactorRequired: false };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  /**
   * Register new user
   */
  async register(userData) {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Registration failed');
      }

      const data = await response.json();
      
      // Registration doesn't auto-login, so no tokens to store
      return data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  /**
   * Logout user - calls backend to clear cookies
   */
  async logout() {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Important to send cookies
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('companyId');
    localStorage.removeItem('platformRole');
    localStorage.removeItem('companyRole');
    
    // Redirect to login
    window.location.href = '/login';
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Check if user is authenticated - cookie-based
   */
  isAuthenticated() {
    // Check if user data exists in localStorage
    // The actual token is in HTTP-only cookie
    return !!this.getCurrentUser();
  },

  /**
   * Get company ID
   */
  getCompanyId() {
    return localStorage.getItem('companyId');
  },

  /**
   * Get user's platform role
   */
  getPlatformRole() {
    return localStorage.getItem('platformRole');
  },

  /**
   * Get user's company role
   */
  getCompanyRole() {
    return localStorage.getItem('companyRole');
  },

  /**
   * Check if user is platform staff (admin portal access)
   */
  isPlatformStaff() {
    const platformRole = this.getPlatformRole();
    return platformRole === 'PLATFORM_OWNER' || platformRole === 'PLATFORM_ADMIN';
  },

  /**
   * Determine which portal user should be redirected to
   */
  getPortalUrl() {
    if (this.isPlatformStaff()) {
      // Platform staff goes to admin portal
      return process.env.REACT_APP_ADMIN_PORTAL_URL || 'http://localhost:3002';
    } else {
      // Customers go to customer portal
      return process.env.REACT_APP_CUSTOMER_PORTAL_URL || 'http://localhost:3001';
    }
  },

  /**
   * Verify email address using token from the verification link
   */
  async verifyEmail(token) {
    const response = await fetch(`${API_URL}/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Email verification failed');
    }
    return data;
  },

  /**
   * Request a password reset email
   */
  async forgotPassword(email) {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to send reset email');
    }
    return data;
  },

  /**
   * Reset password using the token from the email link
   */
  async resetPassword(token, newPassword) {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to reset password');
    }
    return data;
  },

  /**
   * Resend verification email to the given address
   */
  async resendVerification(email) {
    const response = await fetch(`${API_URL}/api/auth/resend-verification?email=${encodeURIComponent(email)}`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to resend verification email');
    }
    return data;
  },
};